import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';

import { Button } from '@llura/ui';

import { signIn } from '../../lib/auth-client';
import { GOOGLE_OAUTH_ENABLED } from '../../lib/config';
import { useStrings } from '../../hooks/useStrings';

export function LoginPage() {
  const navigate = useNavigate();
  const { strings } = useStrings();
  const t = strings.auth.login;
  const oauth = strings.auth.oauth;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { error: signInError } = await signIn.email({ email, password });
      if (signInError) {
        const msg = signInError.message ?? '';
        if (/verif/i.test(msg)) setError(t.errorUnverified);
        else if (/invalid|incorrect|password/i.test(msg)) setError(t.errorInvalid);
        else setError(msg || t.errorGeneric);
        setBusy(false);
        return;
      }
      navigate('/', { replace: true });
    } catch {
      setError(t.errorGeneric);
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-[100svh] overflow-hidden bg-background">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-[-15%] h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-10%] left-[-10%] h-[380px] w-[380px] rounded-full bg-secondary/10 blur-3xl"
      />

      <header className="sticky top-0 z-10 flex items-center gap-2 bg-background/80 px-4 py-3 backdrop-blur">
        <span className="font-serif text-lg">{strings.brand.name}</span>
      </header>

      <main className="relative mx-auto w-full max-w-md px-5 pb-16 pt-8">
        <h1 className="font-serif text-3xl">{t.title}</h1>

        {GOOGLE_OAUTH_ENABLED && (
          <>
            <Button
              variant="secondary"
              className="mt-8 w-full"
              onClick={() =>
                void signIn.social({ provider: 'google', callbackURL: `${window.location.origin}/` })
              }
            >
              {oauth.googleCta}
            </Button>
            <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              {oauth.orSeparator}
              <span className="h-px flex-1 bg-border" />
            </div>
          </>
        )}

        <form onSubmit={submit} className={GOOGLE_OAUTH_ENABLED ? 'space-y-4' : 'mt-8 space-y-4'}>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t.emailLabel}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">{t.passwordLabel}</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>

          {error && (
            <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
          )}

          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? t.submitting : t.submitCta}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t.signupPrompt}{' '}
          <Link to="/signup" className="text-primary underline-offset-4 hover:underline">
            {t.signupLink}
          </Link>
        </p>
      </main>
    </div>
  );
}
